import React, { useState } from 'react';

interface FormData {
  name: string;
  gender: string;
  phone: string;
  city: string;
  loanType: string;
  employment: string;
  contactTime: string;
}

const ApplicationForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    gender: '',
    phone: '',
    city: '',
    loanType: '',
    employment: '',
    contactTime: ''
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 清除對應欄位的錯誤訊息
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = '請輸入姓名';
    }

    if (!formData.gender) {
      newErrors.gender = '請選擇性別';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = '請輸入手機號碼';
    } else if (!/^09\d{8}$/.test(formData.phone)) {
      newErrors.phone = '手機號碼格式不正確';
    }

    if (!formData.city) {
      newErrors.city = '請選擇居住城市';
    }

    if (!formData.loanType) {
      newErrors.loanType = '請選擇貸款類型';
    }

    if (!formData.employment) {
      newErrors.employment = '請選擇職業狀態';
    }

    if (!formData.contactTime) {
      newErrors.contactTime = '請選擇方便聯繫時間';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // 將表單數據暫存到本地存儲
      localStorage.setItem('pendingForm', JSON.stringify(formData));
      
      // 顯示成功訊息
      setShowSuccess(true);
      
      // 2秒後跳轉到 LINE 官方帳號
      setTimeout(() => {
        window.open('https://line.me/R/ti/p/@939rzwem', '_blank');
        setShowSuccess(false);
        // 重置表單
        setFormData({
          name: '',
          gender: '',
          phone: '',
          city: '',
          loanType: '',
          employment: '',
          contactTime: ''
        });
      }, 2000);
      
    } catch (error) {
      console.error('提交表單時發生錯誤:', error);
      alert('提交失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cities = [
    '台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市',
    '基隆市', '新竹市', '新竹縣', '苗栗縣', '彰化縣', '南投縣',
    '雲林縣', '嘉義市', '嘉義縣', '屏東縣', '宜蘭縣', '花蓮縣', '台東縣'
  ];

  const loanTypes = [
    '個人信貸', '房屋貸款', '企業融資', '汽車貸款', '債務整合', '其他'
  ];

  const employmentTypes = [
    '上班族', '自營商', '專業人士', '家庭主婦', '學生', '退休', '其他'
  ];

  const contactTimes = [
    '上午 9:00-12:00', '下午 13:00-17:00', '晚上 18:00-21:00', '全天皆可'
  ];

  return (
    <section id="application-form" className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            立即申請貸款
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            填寫以下表單，我們將盡快與您聯繫，提供最適合您的貸款方案
          </p>
        </div>

        {showSuccess && (
          <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-green-700 font-medium">
                表單提交成功！正在為您轉接到 LINE 官方帳號...
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 姓名 */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                姓名 *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="請輸入您的姓名"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* 性別 */}
            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                性別 *
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.gender ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">請選擇性別</option>
                <option value="male">男</option>
                <option value="female">女</option>
              </select>
              {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
            </div>

            {/* 手機號碼 */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                手機號碼 *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="09xxxxxxxx"
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
            </div>

            {/* 居住城市 */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                居住城市 *
              </label>
              <select
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.city ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">請選擇居住城市</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
              {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
            </div>

            {/* 貸款類型 */}
            <div>
              <label htmlFor="loanType" className="block text-sm font-medium text-gray-700 mb-2">
                貸款類型 *
              </label>
              <select
                id="loanType"
                name="loanType"
                value={formData.loanType}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.loanType ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">請選擇貸款類型</option>
                {loanTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.loanType && <p className="text-red-500 text-sm mt-1">{errors.loanType}</p>}
            </div>

            {/* 職業狀態 */}
            <div>
              <label htmlFor="employment" className="block text-sm font-medium text-gray-700 mb-2">
                職業狀態 *
              </label>
              <select
                id="employment"
                name="employment"
                value={formData.employment}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.employment ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">請選擇職業狀態</option>
                {employmentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.employment && <p className="text-red-500 text-sm mt-1">{errors.employment}</p>}
            </div>
          </div>

          {/* 方便聯繫時間 */}
          <div className="mt-6">
            <label htmlFor="contactTime" className="block text-sm font-medium text-gray-700 mb-2">
              方便聯繫時間 *
            </label>
            <select
              id="contactTime"
              name="contactTime"
              value={formData.contactTime}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.contactTime ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">請選擇方便聯繫時間</option>
              {contactTimes.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
            {errors.contactTime && <p className="text-red-500 text-sm mt-1">{errors.contactTime}</p>}
          </div>

          {/* 提交按鈕 */}
          <div className="mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-md text-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? '提交中...' : '立即申請'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              點擊提交後，將自動跳轉至 LINE 官方帳號進行後續服務
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ApplicationForm;